import React, { useState } from 'react';
import { Star, Link2, Check, CalendarCheck, CornerDownRight } from 'lucide-react';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '../ui/accordion';
import { FaqHelpfulVote } from './FaqHelpfulVote';

const formatDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  } catch {
    return iso;
  }
};

const CopyLinkButton = ({ slug }) => {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    const url = `${window.location.origin}/faq#${slug}`;
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      window.prompt('Copy this link', url);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };
  return (
    <button
      type="button"
      data-testid={`faq-copy-link-${slug}`}
      onClick={copy}
      className="inline-flex items-center gap-1 text-xs font-medium text-[#B5541B] hover:underline"
    >
      {copied ? <Check className="w-3.5 h-3.5" /> : <Link2 className="w-3.5 h-3.5" />}
      {copied ? 'Link copied' : 'Copy link'}
    </button>
  );
};

export const FaqList = ({ items, openSlug, onOpenChange, getRelated, onGoTo, relatedHeading = 'Related questions', helpfulContent, onAskInstead }) => (
  <Accordion type="single" collapsible value={openSlug || ''} onValueChange={onOpenChange} className="space-y-3" data-testid="faq-list">
    {items.map((item) => {
      const related = getRelated ? getRelated(item) : [];
      return (
      <AccordionItem
        key={item.id}
        value={item.slug}
        id={item.slug}
        data-testid={`faq-item-${item.slug}`}
        className={`bg-white rounded-2xl border px-5 sm:px-6 shadow-sm transition-colors data-[state=open]:border-[#B5541B]/40 ${
          item.featured ? 'border-[#D4722F]/40' : 'border-gray-100'
        }`}
      >
        <AccordionTrigger className="text-left text-base sm:text-lg font-semibold text-[#1F1E1E] hover:text-[#B5541B] hover:no-underline py-5 gap-4" data-testid={`faq-trigger-${item.slug}`}>
          <span className="flex items-start gap-2.5">
            {item.featured && <Star className="w-4 h-4 mt-1 shrink-0 text-[#D4722F] fill-[#D4722F]" aria-label="Featured" />}
            <span>{item.question}</span>
          </span>
        </AccordionTrigger>
        <AccordionContent className="pb-6">
          <p className="text-[15px] sm:text-base leading-relaxed text-[#3B3535] whitespace-pre-line" data-testid={`faq-answer-${item.slug}`}>
            {item.answer}
          </p>
          {(item.topics.length > 0 || item.audience.length > 0) && (
            <div className="flex flex-wrap gap-1.5 mt-4">
              {item.audience.map((a) => (
                <span key={a} className="text-xs font-medium px-2 py-0.5 rounded-full bg-[#1F1E1E]/5 text-[#3B3535]">{a}</span>
              ))}
              {item.topics.map((t) => (
                <span key={t} className="text-xs font-medium px-2 py-0.5 rounded-full bg-[#B5541B]/10 text-[#B5541B]">{t}</span>
              ))}
            </div>
          )}
          <div className="flex flex-wrap items-center justify-between gap-3 mt-4 pt-3 border-t border-gray-100">
            {item.last_reviewed ? (
              <span className="inline-flex items-center gap-1 text-xs text-[#3B3535]/60" data-testid={`faq-reviewed-${item.slug}`}>
                <CalendarCheck className="w-3.5 h-3.5" /> Last reviewed: {formatDate(item.last_reviewed)}
              </span>
            ) : <span />}
            <CopyLinkButton slug={item.slug} />
          </div>
          {helpfulContent && <FaqHelpfulVote item={item} content={helpfulContent} onAskInstead={onAskInstead} />}
          {related.length > 0 && (
            <div className="mt-4 rounded-xl bg-[#FFF8F5] border border-[#B5541B]/10 p-4" data-testid={`faq-related-${item.slug}`}>
              <p className="text-xs font-semibold uppercase tracking-wide text-[#B5541B] mb-2">{relatedHeading}</p>
              <ul className="space-y-1.5">
                {related.map((r) => (
                  <li key={r.id}>
                    <button
                      type="button"
                      data-testid={`faq-related-link-${item.slug}--${r.slug}`}
                      onClick={() => onGoTo(r.slug)}
                      className="inline-flex items-start gap-2 text-left text-sm font-medium text-[#1F1E1E] hover:text-[#B5541B] transition-colors"
                    >
                      <CornerDownRight className="w-3.5 h-3.5 mt-1 shrink-0 text-[#B5541B]/60" />
                      <span>{r.question}</span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </AccordionContent>
      </AccordionItem>
      );
    })}
  </Accordion>
);
